import type { MapPoint } from '@/shared/mobile-service/lib/map-points';

/**
 * Zoom at which a coalesced marker opens the prev/next stack.
 *
 * Below this, a click steps the camera in rather than jumping to the split.
 */
export const CLUSTER_STACK_ZOOM = 16;

/**
 * Zoom levels added by one cluster click.
 *
 * Each integer level doubles the map scale, so this step is four times closer.
 * A fixed step drills into a dense cluster without leaping to the level where
 * every point separates.
 */
export const CLUSTER_ZOOM_STEP = 2;

/**
 * Keep coalescing markers that still overlap through the top of the map zoom.
 * The GeoJSON source max zoom must be greater than this or MapLibre refuses to cluster.
 */
export const CLUSTER_MAX_ZOOM = 22;
export const CLUSTER_SOURCE_MAX_ZOOM = CLUSTER_MAX_ZOOM + 1;

/** Cluster leaf from MapLibre. Point coordinates are `[longitude, latitude]`. */
export interface ClusterLeaf {
  geometry?: unknown;
  properties?: Record<string, unknown> | null;
}

export interface StackBadge {
  stackCount: number;
  /** True only for the first point in a coincident group larger than one. */
  showLabel: boolean;
}

function coordinateKey(latitude: number, longitude: number): string {
  return `${latitude},${longitude}`;
}

function leafCoordinate(
  leaf: ClusterLeaf,
): { latitude: number; longitude: number } | null {
  const geometry = leaf.geometry;
  if (geometry == null || typeof geometry !== 'object' || !('coordinates' in geometry)) return null;
  if ('type' in geometry && geometry.type != null && geometry.type !== 'Point') return null;
  const coordinates = geometry.coordinates;
  if (!Array.isArray(coordinates) || coordinates.length < 2) return null;
  const longitude = coordinates[0];
  const latitude = coordinates[1];
  if (typeof longitude !== 'number' || typeof latitude !== 'number') return null;
  if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return null;
  return { latitude, longitude };
}

/**
 * Every plotted point at this exact coordinate, in the existing point order.
 *
 * Nearby coordinates stay out. The full point list is the source of truth so a
 * repeated id cannot collapse the stack.
 */
export function pointsAtCoordinate(
  points: MapPoint[],
  latitude: number,
  longitude: number,
): MapPoint[] {
  return points.filter(
    (point) => point.latitude === latitude && point.longitude === longitude,
  );
}

/** True when every leaf sits on one coordinate. An empty set is not a stack. */
export function sameCoordinate(leaves: ClusterLeaf[]): boolean {
  const first = leaves.length > 0 ? leafCoordinate(leaves[0]) : null;
  if (!first) return false;
  return leaves.every((leaf) => {
    const coordinate = leafCoordinate(leaf);
    return (
      coordinate != null &&
      coordinate.latitude === first.latitude &&
      coordinate.longitude === first.longitude
    );
  });
}

function leafPointIndex(leaf: ClusterLeaf): number | null {
  const raw = leaf.properties?.pointIndex;
  const index = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : Number.NaN;
  if (!Number.isInteger(index) || index < 0) return null;
  return index;
}

/**
 * Camera zoom after a cluster click.
 *
 * MapLibre's expansion zoom is where the cluster fully splits. For points that
 * sit near each other that is close to the top of the scale, so a click feels
 * like it jumped to the pins. Advance {@link CLUSTER_ZOOM_STEP} levels, and
 * stop at the expansion zoom when the cluster separates sooner. A non-finite
 * expansion zoom is returned unchanged so the caller can skip the move.
 */
export function nextClusterZoom(currentZoom: number, expansionZoom: number): number {
  if (!Number.isFinite(currentZoom) || !Number.isFinite(expansionZoom)) return expansionZoom;
  return Math.min(currentZoom + CLUSTER_ZOOM_STEP, expansionZoom);
}

/**
 * True when a cluster click should page through its points.
 *
 * An exact pile of coordinates can never be pulled apart, so it opens the
 * stack at any zoom. Otherwise the stack opens at {@link CLUSTER_STACK_ZOOM}
 * and whenever zooming would not split the marker.
 */
export function clusterClickOpensStack(
  zoom: number,
  expansionZoom: number,
  leavesAreCoincident: boolean,
): boolean {
  if (leavesAreCoincident) return true;
  if (!Number.isFinite(zoom) || zoom >= CLUSTER_STACK_ZOOM) return true;
  if (!Number.isFinite(expansionZoom) || expansionZoom <= zoom) return true;
  return false;
}

/**
 * Plotted points inside a cluster, in the original point order.
 *
 * Leaf `pointIndex` is the source of truth so repeated ids and nearby
 * coordinates both stay in the stack. Without indexes, each distinct leaf
 * coordinate contributes every plotted point sitting on it.
 */
export function pointsForClusterLeaves(points: MapPoint[], leaves: ClusterLeaf[]): MapPoint[] {
  const indexes: number[] = [];
  const seen = new Set<number>();
  for (const leaf of leaves) {
    const index = leafPointIndex(leaf);
    if (index == null || index >= points.length || seen.has(index)) continue;
    seen.add(index);
    indexes.push(index);
  }
  if (indexes.length > 0) {
    indexes.sort((left, right) => left - right);
    return indexes.map((index) => points[index]);
  }

  const stacked: MapPoint[] = [];
  const seenKeys = new Set<string>();
  for (const leaf of leaves) {
    const coordinate = leafCoordinate(leaf);
    if (!coordinate) continue;
    const key = coordinateKey(coordinate.latitude, coordinate.longitude);
    if (seenKeys.has(key)) continue;
    seenKeys.add(key);
    stacked.push(...pointsAtCoordinate(points, coordinate.latitude, coordinate.longitude));
  }
  return stacked;
}

/**
 * Points to show when a cluster cannot be pulled apart.
 *
 * Mixed leaves return null so the map zooms instead. An exact stack returns
 * every plotted point at that coordinate.
 */
export function exactStackAt(points: MapPoint[], leaves: ClusterLeaf[]): MapPoint[] | null {
  if (!sameCoordinate(leaves)) return null;
  const coordinate = leafCoordinate(leaves[0]);
  if (!coordinate) return null;
  const stack = pointsAtCoordinate(points, coordinate.latitude, coordinate.longitude);
  return stack.length > 0 ? stack : null;
}

/** Per-point stack size, with the count label reserved for the first sibling. */
export function stackBadges(points: MapPoint[]): StackBadge[] {
  const counts = new Map<string, number>();
  for (const point of points) {
    const key = coordinateKey(point.latitude, point.longitude);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const labeled = new Set<string>();
  return points.map((point) => {
    const key = coordinateKey(point.latitude, point.longitude);
    const stackCount = counts.get(key) ?? 1;
    const showLabel = stackCount > 1 && !labeled.has(key);
    if (showLabel) labeled.add(key);
    return { stackCount, showLabel };
  });
}
